// src/ui/refusal-view.ts
// A REFUSAL, ON SCREEN, AS THE THING IT ACTUALLY IS.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE RULE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The headline is a short label this app chose. The BODY is the server's own sentence, verbatim.
// This module never paraphrases, never softens, and never substitutes a friendlier message for one
// the backend deliberately wrote — the kill switch's `message` exists so an operator at 2am learns
// which switch is off, and replacing it with "Service temporarily unavailable" throws that away.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// AND THE RULE'S SHARP EDGE: "TRY AGAIN" IS NOT OFFERED WHERE IT WOULD BE A LIE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A stopped lab, an unconfigured bucket and an unmounted route do not change because a phone asked
// twice. Those render with NO retry button and with the operator action named instead. Offering a
// button that cannot work is how a deliberate stop gets mistaken for a flaky network, and how a
// tester spends an afternoon on it.

import { el } from './dom';
import type { Refusal } from '../api/refusal';
import { neverSucceedsOnRetry } from '../api/refusal';

/** Short label per kind. The DETAIL always comes from the server; only this line is ours. */
const HEADLINE: Record<Refusal['kind'], string> = {
  unauthenticated: 'Not signed in',
  forbidden: 'Not allowed',
  not_mounted: 'Not built in this deployment',
  not_found: 'Not found',
  conflict: 'Refused',
  invalid_request: 'Rejected as malformed',
  lab_stopped: 'Deliberately stopped',
  upload_not_configured: 'Video upload is not enabled yet',
  upload_misconfigured: 'Video storage is misconfigured',
  service_unavailable: 'Unavailable',
  server_error: 'The server failed',
  unreachable: 'Could not reach the server',
  unknown_refusal: 'Unrecognised response',
};

/**
 * The operator action, per kind — named so nobody has to guess who can fix it.
 *
 * These sentences describe the DEPLOYMENT, not the user, and they are the only place this module
 * adds words the server did not send. They are safe to add because they are about our own
 * infrastructure, not about what the server decided.
 */
function operatorAction(r: Refusal): string | null {
  switch (r.kind) {
    case 'upload_not_configured':
      return r.missing.length
        ? `Someone with access to the deployment must set: ${r.missing.join(', ')}. `
          + 'Until then this app deliberately does not offer video upload.'
        : 'Someone with access to the deployment must configure R2 storage.';
    case 'upload_misconfigured':
      return 'Storage IS configured, but inconsistently — uploads would land where consent '
        + 'revocation does not delete. This must be fixed before any video is accepted.';
    case 'lab_stopped':
      return r.switchName
        ? `An operator can re-enable it by clearing ${r.switchName}. Nothing recorded before the stop has been deleted.`
        : 'An operator turned this off. Nothing recorded before the stop has been deleted.';
    case 'not_mounted':
      // As of 26 Aug 2026 the /v1/practice namespace IS mounted in production — this refusal should
      // not appear. It is kept, and kept accurate, because the state it describes is one rollback
      // away, and because the distinction it draws (not built ≠ not allowed) is the whole reason the
      // gateway made `unmounted` machine-readable. What it must NOT do is name a branch: pointing at
      // a specific undeployed branch was true last week and would now send someone to merge a branch
      // that is already in.
      return 'This route is not in the running build of the server. That is a deployment gap, not a '
        + 'permission decision — no change on this phone can reach it. Someone with access to the '
        + 'backend deployment must ship the build that contains it.';
    default:
      return null;
  }
}

export interface RefusalViewOptions {
  /** Rendered only when retrying could actually change the answer. */
  onRetry?: () => void;
  onSignIn?: () => void;
}

export function refusalView(r: Refusal, opts: RefusalViewOptions = {}): HTMLElement {
  const operator = operatorAction(r);
  const box = el('div', { class: `refusal refusal-${r.kind}`, role: 'alert' });

  box.append(
    el('p', { class: 'refusal-headline' }, HEADLINE[r.kind]),
    // ── THE SERVER'S OWN WORDS. Verbatim. This line is the point of the whole module.
    el('p', { class: 'refusal-message' }, r.message),
  );

  // The kill switch names the responsible switch; show it, because that is what an operator needs.
  if (r.switchName) {
    box.append(el('p', { class: 'refusal-switch' }, `Switch: ${r.switchName}`));
  }
  if (r.missing.length) {
    box.append(el('p', { class: 'refusal-missing' }, `Missing configuration: ${r.missing.join(', ')}`));
  }
  // "This is a deliberate stop, not a fault." — the gateway wrote that sentence; carry it.
  if (r.note) {
    box.append(el('p', { class: 'refusal-note' }, r.note));
  }
  if (operator) {
    box.append(el('p', { class: 'refusal-operator' }, operator));
  }

  const actions = el('div', { class: 'refusal-actions' });
  if (r.kind === 'unauthenticated' && opts.onSignIn) {
    actions.append(el('button', { class: 'btn', onclick: opts.onSignIn }, 'Enter a token'));
  }
  // NO retry button where retrying is a lie. See the header block.
  // Hide "Try again" when the answer cannot change. Offering it on a malformed frozen
  // payload invites exactly the loop that reached 84 attempts.
  if (opts.onRetry && !neverSucceedsOnRetry(r)) {
    actions.append(el('button', { class: 'btn', onclick: opts.onRetry }, 'Try again'));
  }
  if (actions.childElementCount) box.append(actions);

  // The raw body, collapsed. Not the headline, but never thrown away: an unrecognised refusal must
  // be inspectable, or the app becomes the thing standing between a tester and the truth.
  box.append(
    el('details', { class: 'refusal-raw' },
      el('summary', {}, `${r.status || 'no response'} · ${r.request}`),
      el('pre', {}, JSON.stringify(r.body, null, 2))),
  );
  return box;
}

/**
 * The one-line form, for a queue row or a list item.
 *
 * Also the server's sentence — just shorter framing around it.
 */
export function refusalLine(r: Refusal): HTMLElement {
  return el('p', { class: `refusal-inline refusal-${r.kind}` },
    el('strong', {}, `${HEADLINE[r.kind]}: `), r.message);
}
